import React, { useState, useEffect } from 'react';
import designService from '../../services/designService';
import DesignForm from './DesignForm';
import { useNavigate } from 'react-router-dom';
import './DesignList.css';

const DesignList = ({ onDesignSelect, hasDesignInteractionAccess }) => {
    const [designs, setDesigns] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredDesigns, setFilteredDesigns] = useState([]);
    const [selectedDesignId, setSelectedDesignId] = useState(null);
	const [isCreatingDesign, setIsCreatingDesign] = useState(false);
	const [editingNameId, setEditingNameId] = useState(null);
    const [editedName, setEditedName] = useState('');
	const [editingDescriptionId, setEditingDescriptionId] = useState(null);
	const [editedDescription, setEditedDescription] = useState('');
	const navigate = useNavigate();

    useEffect(() => {
        const loadDesigns = async () => {
            try {
                const data = await designService.fetchDesigns();
                setDesigns(data);
                setFilteredDesigns(data); // Initially show all designs
            } catch (error) {
                console.error('Error fetching designs:', error);
            }
        };

        loadDesigns();
    }, []);

    useEffect(() => {
        // Filter designs based on searchTerm
        const results = designs.filter(design =>
            design.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            design.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            design.tags.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredDesigns(results);
    }, [searchTerm, designs]);

    // Handle design click for selection (only if interactive access)
    const handleDesignClick = (designId) => {
        if (hasDesignInteractionAccess) {
            setSelectedDesignId(designId);
            if (onDesignSelect) {
                onDesignSelect(designId);
            }
        } else {
            console.log("Trial ended. Upgrade to interact with designs.");
        }
    };
	
    const handleCreateDesignClick = () => {
        if (!hasDesignInteractionAccess) {
            alert("Your free trial has ended. Please subscribe to a premium plan to create new designs.");
            return;
        }
        setIsCreatingDesign(true); // Show the form
    };
	
	const handleCancelCreateDesign = () => {
        setIsCreatingDesign(false); // Hide the form
    };
	
	const handleEditName = (event, designId, name) => {
           event.stopPropagation();
           setEditingNameId(designId);
           setEditedName(name);
       };
   
    const handleSaveName = (event, designId) => {
           event.stopPropagation();
           const updatedDesigns = designs.map(design =>
               design.id === designId ? { ...design, name: editedName } : design
           );
           setDesigns(updatedDesigns);
           setFilteredDesigns(updatedDesigns);
           setEditingNameId(null);
       };
   
    const handleEditDescription = (event, designId, description) => {
           event.stopPropagation();
           setEditingDescriptionId(designId);
           setEditedDescription(description);
       };
   
    const handleSaveDescription = (event, designId) => {
           event.stopPropagation();
           const updatedDesigns = designs.map(design =>
               design.id === designId ? { ...design, description: editedDescription } : design
           );
           setDesigns(updatedDesigns);
           setFilteredDesigns(updatedDesigns);
           setEditingDescriptionId(null);
       };
   
       const handleDeleteDesign = async (event, designId) => {
			event.stopPropagation();
		           
		    if (!hasDesignInteractionAccess) {
				alert("Your free trial has ended. Please subscribe to a premium plan to delete designs.");
				return;
			}
           const confirmed = window.confirm("Are you sure you want to delete this design?");
           if (confirmed) {
               try {
                   await designService.deleteDesign(designId);
                   const updatedDesigns = designs.filter(design => design.id !== designId);
                   setDesigns(updatedDesigns);
                   setFilteredDesigns(updatedDesigns);
               } catch (error) {
                   console.error('Error deleting design:', error);
                   alert('Could not delete design.');
               }
           }
       };
   
       const handleDesignDetailsClick = (event, designId) => {
           event.stopPropagation();
		   if (!hasDesignInteractionAccess) {
				alert("Your free trial has ended. Please subscribe to a premium plan to enter the design canvas.");
				return;
			}
           navigate(`/design/${designId}/`);
       };
   
	   
	return (
           <div className="design-list-container">
               <div className="design-list-header">
                   <button onClick={handleCreateDesignClick}
					className="create-design-button"
					disabled={!hasDesignInteractionAccess}
					title={!hasDesignInteractionAccess ? "Upgrade to create new designs" : ""}
				   >
					Create New Design
				   </button>
               </div>
               <input
                   type="text"
                   placeholder="Search designs..."
                   value={searchTerm}
                   onChange={(e) => setSearchTerm(e.target.value)}
                   className="design-search-input"
               />
               {isCreatingDesign && (
                   <DesignForm onDesignCreated={handleCreateDesignClick} onCancel={handleCancelCreateDesign} />
               )}
               <ul className="design-list">
                   {filteredDesigns.map(design => (
                       <li
                           key={design.id}
                           // Clickable only if hasDesignInteractionAccess
						   onClick={hasDesignInteractionAccess ? () => handleDesignClick(design.id) : null}
                           className={`design-list-item ${selectedDesignId === design.id ? 'selected-design' : ''} ${!hasDesignInteractionAccess ? 'disabled-item' : ''}`}
						   title={!hasDesignInteractionAccess ? "Upgrade to interact with this design" : ""}
                       >
                           <div className="design-item-header">
                               {editingNameId === design.id ? (
                                   <input
                                       value={editedName}
                                       onChange={(e) => setEditedName(e.target.value)}
                                       onBlur={(e) => handleSaveName(e, design.id)}
                                       onKeyDown={(e) => {
                                           if (e.key === 'Enter') {
                                               handleSaveName(e, design.id);
                                           }
                                       }}
                                       style={{ width: '100%' }}
                                   />
                               ) : (
                                   <span onClick={(e) => handleEditName(e, design.id, design.name)}>
                                       {design.name}
                                   </span>
                               )}
                           </div>
                           <div className="design-item-details">
                               {editingDescriptionId === design.id ? (
                                   <div>
                                       <textarea
                                           value={editedDescription}
                                           onChange={(e) => setEditedDescription(e.target.value)}
                                           onBlur={(e) => handleSaveDescription(e, design.id)}
                                           onKeyDown={(e) => {
                                               if (e.key === 'Enter') {
                                                   handleSaveDescription(e, design.id);
                                               }
                                           }}
                                           style={{ width: '100%' }}
                                       />
                                   </div>
                               ) : (
                                   <p onClick={(e) => handleEditDescription(e, design.id, design.description)}>
                                       {design.description}
                                   </p>
                               )}
                               <div className="design-item-buttons">
                                   <button
										onClick={(e) => handleDesignDetailsClick(e, design.id)}
										className="enter-canvas-button"
										disabled={!hasDesignInteractionAccess}
										title={!hasDesignInteractionAccess ? "Upgrade to enter canvas" : ""}>
                                       &gt;
                                   </button>
                                   <button 
								   onClick={(e) => handleDeleteDesign(e, design.id)}
								   className="delete-design-button"
								   disabled={!hasDesignInteractionAccess}
								   title={!hasDesignInteractionAccess ? "Upgrade to delete designs" : ""}>
                                       X
                                   </button>
                               </div>
                           </div>
                       </li>
                   ))}
               </ul>
           </div>
       );
   };

export default DesignList;